import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

/** Simple SVG car silhouette used as fallback when Wikipedia image is missing/broken. */
const CarSilhouette = ({ className = "" }) => (
  <svg viewBox="0 0 128 80" className={className}>
    <path
      d="M8 60 Q22 34 48 34 L80 34 Q102 40 120 60 L120 68 L100 68 Q95 56 88 56 Q81 56 76 68 L44 68 Q39 56 32 56 Q25 56 20 68 L8 68 Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <g stroke="currentColor" strokeWidth="1.4" fill="none">
      <circle cx="32" cy="66" r="8" />
      <circle cx="88" cy="66" r="8" />
    </g>
  </svg>
);

// Stable brand-accent colour palette - derived hash of brand string.
function brandAccent(brand = "") {
  const palette = [
    "#2FA8D7", "#5BC4E8", "#247A4B", "#6B7280", "#EEF2F5",
    "#EF1A2D", "#0166B1", "#FF8000", "#EAB308", "#EC4899",
    "#22D3EE", "#F97316", "#84CC16", "#8B5CF6", "#F43F5E",
  ];
  let hash = 0;
  for (let i = 0; i < brand.length; i++) hash = (hash * 31 + brand.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
}

export function CarCard({ car, index = 0 }) {
  const accent = brandAccent(car.brand);
  const [imgFailed, setImgFailed] = useState(false);
  const testKey = `${car.wd_id || car.name}`.toLowerCase().replace(/[^a-z0-9-]+/g, "-");

  return (
    <motion.article
      data-testid={`car-card-${testKey}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: Math.min(index * 0.008, 0.12) }}
      className="group relative border border-white/10 bg-obsidian/70 hover:border-white/25 transition-all overflow-hidden"
    >
      <Link
        to={car.wd_id ? `/console/car/${car.wd_id}` : "#"}
        data-testid={`car-decode-btn-${testKey}`}
        className="absolute inset-0 z-20"
        aria-label={`Register ${car.brand} ${car.model} in your garage`}
      />
      {/* Photo band */}
      <div className="relative aspect-[4/3] bg-black overflow-hidden">
        {imgFailed || !car.image_url ? (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ color: accent }}
          >
            <CarSilhouette className="w-3/5 h-3/5 opacity-70" />
          </div>
        ) : (
          <img
            src={car.image_url}
            alt={car.name || `${car.brand} ${car.model}`}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setImgFailed(true)}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        )}

        {/* Gradient wash */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-obsidian via-obsidian/60 to-transparent" />

        {/* Country badge */}
        {car.country && car.country !== "Unknown" && (
          <span
            className="absolute top-3 left-3 font-mono-tech text-[9px] uppercase tracking-widest px-2 py-1 border bg-black/50 backdrop-blur"
            style={{ color: accent, borderColor: `${accent}66` }}
          >
            {car.country}
          </span>
        )}

        {/* Brand letter watermark */}
        <div
          className="pointer-events-none absolute -right-3 -top-3 font-display text-[110px] leading-none font-semibold select-none opacity-[0.14] group-hover:opacity-[0.22] transition-opacity"
          style={{ color: accent }}
        >
          {(car.brand || "?")[0].toUpperCase()}
        </div>
      </div>

      {/* Meta */}
      <div className="relative p-5">
        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">
          {car.brand}
        </div>
        <div
          className="mt-1 font-display text-lg font-semibold text-white tracking-tighter leading-tight line-clamp-1"
          title={car.model}
        >
          {car.model}
        </div>

        <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between">
          <span
            className="font-mono-tech text-[9px] uppercase tracking-widest opacity-70"
            style={{ color: accent }}
          >
            ● live
          </span>
          <button
            data-testid={`car-decode-cta-${testKey}`}
            tabIndex={-1}
            className="font-mono-tech text-[9px] uppercase tracking-widest text-white/60 group-hover:text-white transition-colors"
          >
            decode → register
          </button>
        </div>
      </div>

      {/* Hover accent glow */}
      <div
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ boxShadow: `inset 0 0 60px ${accent}22` }}
      />
    </motion.article>
  );
}

export default CarCard;
